import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, Circle, Clock, AlertCircle, Pencil, Trash2, Calendar, User, List, Repeat, ExternalLink, Timer, Link2, DollarSign } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import SubtaskButton from "./SubtaskButton";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";

const statusConfig = {
  todo: { icon: Circle, color: "text-slate-400", bg: "bg-slate-100 text-slate-700", label: "Å Gjøre" },
  in_progress: { icon: Clock, color: "text-blue-500", bg: "bg-blue-100 text-blue-700", label: "Pågår" },
  completed: { icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-100 text-emerald-700", label: "Fullført" },
  cancelled: { icon: AlertCircle, color: "text-rose-500", bg: "bg-rose-100 text-rose-700", label: "Kansellert" },
};

const priorityColors = {
  low: "border-slate-200",
  medium: "border-blue-300",
  high: "border-amber-400",
  urgent: "border-rose-500",
};

const priorityBadge = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-blue-100 text-blue-700",
  high: "bg-amber-100 text-amber-700",
  urgent: "bg-rose-100 text-rose-700",
};

const priorityLabels = {
  low: "Lav",
  medium: "Middels",
  high: "Høy",
  urgent: "Haster",
};

export default function TasksList({ tasks, subtasks = [], allTasks = [], onEdit, onDelete, onStatusChange, onAddSubtask, timeEntries = [] }) {
  const navigate = useNavigate();
  
  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => base44.entities.User.list(),
  });

  const getUserHourlyRate = (userEmail) => {
    const user = users.find(u => u.email === userEmail);
    return user?.hourly_rate || 0;
  };

  const calculateEstimatedCost = (task) => {
    if (!task.estimated_hours || !task.assigned_to) return 0;
    const hourlyRate = getUserHourlyRate(task.assigned_to);
    return task.estimated_hours * hourlyRate;
  };
  
  const getActualHours = (taskId) => {
    return timeEntries
      .filter(entry => entry.task_id === taskId)
      .reduce((sum, entry) => sum + (entry.duration_minutes || 0) / 60, 0);
  };

  const getSubtaskProgress = (taskId) => {
    const taskSubtasks = subtasks.filter(st => st.parent_task_id === taskId);
    if (taskSubtasks.length === 0) return null;
    const completed = taskSubtasks.filter(st => st.status === "completed").length;
    return {
      total: taskSubtasks.length,
      completed,
      percentage: (completed / taskSubtasks.length) * 100
    };
  };

  const isBlockedByDependencies = (task) => {
    if (!task.depends_on || task.depends_on.length === 0) return false;
    return task.depends_on.some(depId => {
      const depTask = allTasks.find(t => t.id === depId);
      return depTask && depTask.status !== "completed";
    });
  };

  if (!tasks || tasks.length === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        Ingen oppgaver ennå
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {tasks.map((task) => {
        const StatusIcon = statusConfig[task.status]?.icon || Circle;
        const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== "completed";
        const taskSubtasks = subtasks.filter(st => st.parent_task_id === task.id);
        const subtaskProgress = getSubtaskProgress(task.id);
        const isBlocked = isBlockedByDependencies(task);
        const dependencies = task.depends_on?.map(id => allTasks.find(t => t.id === id)).filter(Boolean) || [];
        
        return (
          <Card 
            key={task.id} 
            className={cn(
              "bg-white hover:shadow-md transition-all border-l-4",
              priorityColors[task.priority] || "border-slate-200"
            )}
          >
            <div className="p-4">
              <div className="flex items-start gap-3">
                <button
                  onClick={() => {
                    const newStatus = task.status === "completed" ? "todo" : 
                                     task.status === "todo" ? "in_progress" : "completed";
                    onStatusChange(task, newStatus);
                  }}
                  className="mt-1"
                >
                  <StatusIcon className={cn("w-5 h-5", statusConfig[task.status]?.color)} />
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1">
                      <h4 className={cn(
                        "font-semibold text-slate-900 mb-1",
                        task.status === "completed" && "line-through text-slate-500"
                      )}>
                        {task.title}
                      </h4>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={cn("text-xs border-0", statusConfig[task.status]?.bg)}>
                          {statusConfig[task.status]?.label || task.status}
                        </Badge>
                        <Badge className={cn("text-xs border-0", priorityBadge[task.priority])}>
                          {priorityLabels[task.priority] || task.priority}
                        </Badge>
                        {isOverdue && (
                          <Badge className="text-xs border-0 bg-rose-100 text-rose-700">
                            Forfalt
                          </Badge>
                        )}
                        {task.checklist && task.checklist.length > 0 && (
                          <Badge variant="outline" className="text-xs">
                            <List className="w-3 h-3 mr-1" />
                            {task.checklist.filter(item => item.completed).length}/{task.checklist.length}
                          </Badge>
                        )}
                        {task.is_recurring && (
                          <Badge variant="outline" className="text-xs">
                            <Repeat className="w-3 h-3 mr-1" />
                            Gjentakende
                          </Badge>
                        )}
                        {isBlocked && (
                          <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Blokkert
                          </Badge>
                        )}
                        {dependencies.length > 0 && (
                          <Badge variant="outline" className="text-xs">
                            <Link2 className="w-3 h-3 mr-1" />
                            {dependencies.length}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Link to={createPageUrl("TaskDetail") + `?id=${task.id}`}>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
                        </Button>
                      </Link>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(task)}>
                        <Pencil className="w-3.5 h-3.5 text-slate-400" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onDelete(task.id)}>
                        <Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-rose-600" />
                      </Button>
                    </div>
                  </div>

                  {task.description && (
                    <p className="text-sm text-slate-600 mb-2">{task.description}</p>
                  )}

                  {subtaskProgress && (
                    <div className="mt-3 space-y-1">
                      <div className="flex items-center justify-between text-xs text-slate-600">
                        <span>Underoppgaver</span>
                        <span>{subtaskProgress.completed}/{subtaskProgress.total}</span>
                      </div>
                      <Progress value={subtaskProgress.percentage} className="h-1.5" />
                    </div>
                  )}

                  {dependencies.length > 0 && (
                    <div className="mt-3 space-y-1">
                      <p className="text-xs font-medium text-slate-600">Avhenger av:</p>
                      <div className="space-y-1">
                        {dependencies.map(dep => (
                          <div key={dep.id} className="flex items-center gap-2 text-xs text-slate-500">
                            {dep.status === "completed" ? (
                              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                            ) : (
                              <Circle className="w-3 h-3 text-amber-600" />
                            )}
                            <span className={dep.status === "completed" ? "line-through" : ""}>
                              {dep.title}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {task.checklist && task.checklist.length > 0 && (
                    <div className="space-y-1 mt-2 mb-2">
                      {task.checklist.slice(0, 3).map((item) => (
                        <div key={item.id} className="flex items-center gap-2 text-xs">
                          <Checkbox checked={item.completed} disabled className="h-3 w-3" />
                          <span className={cn(item.completed && "line-through text-slate-400")}>
                            {item.text}
                          </span>
                        </div>
                      ))}
                      {task.checklist.length > 3 && (
                        <p className="text-xs text-slate-400 pl-5">+{task.checklist.length - 3} flere</p>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                    {task.assigned_to_name && (
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        <span>{task.assigned_to_name}</span>
                      </div>
                    )}
                    {task.due_date && (
                      <div className={cn(
                        "flex items-center gap-1",
                        isOverdue && "text-rose-600 font-medium"
                      )}>
                        <Calendar className="w-3 h-3" />
                        <span>{format(new Date(task.due_date), "MMM d, yyyy")}</span>
                      </div>
                    )}
                    {task.estimated_hours && (
                      <Badge variant="outline" className="text-xs flex items-center gap-1">
                        <Timer className="w-3 h-3" />
                        <span>Est: {task.estimated_hours}h</span>
                      </Badge>
                    )}
                    {calculateEstimatedCost(task) > 0 && (
                      <Badge variant="outline" className="text-xs flex items-center gap-1 bg-emerald-50 text-emerald-700 border-emerald-200">
                        <DollarSign className="w-3 h-3" />
                        <span>{calculateEstimatedCost(task).toLocaleString()} kr</span>
                      </Badge>
                    )}
                    {task.estimated_hours && getActualHours(task.id) > 0 && (
                      <div className="flex items-center gap-1 text-xs">
                        <Clock className="w-3 h-3" />
                        <span>
                          {Math.round(getActualHours(task.id))}h / {task.estimated_hours}h
                        </span>
                      </div>
                    )}
                    {task.related_to_name && (
                      <div className="text-zinc-900 font-medium">
                        → {task.related_to_name}
                      </div>
                    )}
                  </div>

                  {taskSubtasks.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-xs text-slate-500 mb-2">Underoppgaver ({taskSubtasks.length})</p>
                      <div className="space-y-1">
                        {taskSubtasks.map((subtask) => (
                          <div key={subtask.id} className="flex items-center gap-2 text-xs pl-2">
                            <Circle className={cn("w-3 h-3", statusConfig[subtask.status]?.color)} />
                            <span className={cn(subtask.status === "completed" && "line-through text-slate-400")}>
                              {subtask.title}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {onAddSubtask && (
              <div className="px-4 pb-3 border-t border-slate-50">
                <SubtaskButton onClick={() => onAddSubtask(task)} />
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}